import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import {
  AtsJobCatalogSelectorGetLevelsByRoleDocument,
  JobCatalogRoleSelectorRolesSearchDocument,
  JobCatalogRoleSelectorAncestorCountsDocument,
  type JobCatalogEnumsJobCatalogTreeNodeTypesEnum,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

const ROLE_PAGE_SIZE = 25
const ANCESTOR_COUNTS_PAGE_SIZE = 50

type RoleRecord = {
  id: string
  name: string
  groupId: string
}

interface Props {
  selectedRoleUuid?: string
  selectedRoleLevelUuid?: string
  onRoleChange: (roleUuid: string | undefined) => void
  onLevelChange?: (levelUuid: string | undefined) => void
  levelError?: string
  showLevelSelector?: boolean
  disabled?: boolean
  readOnly?: boolean
  filters?: Record<string, unknown>
  groupByNodeType?: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
}

export const JobCatalogRoleSelector = ({
  selectedRoleUuid,
  selectedRoleLevelUuid,
  onRoleChange,
  onLevelChange,
  levelError,
  showLevelSelector = true,
  disabled = false,
  readOnly = false,
  filters,
  groupByNodeType,
}: Props) => {
  const [roleSearch, setRoleSearch] = useState('')
  const [internalRoleUuid, setInternalRoleUuid] = useState<string | undefined>(selectedRoleUuid)
  const groupNamesRef = useRef<Map<string, string>>(new Map())
  const previousCountsRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    setInternalRoleUuid(selectedRoleUuid)
  }, [selectedRoleUuid])

  const { data: ancestorCountsData, fetchMore: fetchMoreCounts } = useQuery(
    JobCatalogRoleSelectorAncestorCountsDocument,
    {
      variables: { search: roleSearch || undefined, first: ANCESTOR_COUNTS_PAGE_SIZE },
      fetchPolicy: 'cache-and-network',
    }
  )

  const countsConnection = ancestorCountsData?.jobCatalog.treeNodeAncestorCountsConnection
  useEffect(() => {
    if (!countsConnection) return
    const map = new Map<string, number>()
    countsConnection.nodes.forEach((node) => {
      map.set(node.ancestorUuid, node.count)
    })
    previousCountsRef.current = map
  }, [countsConnection])

  useEffect(() => {
    if (!countsConnection?.pageInfo.hasNextPage || !countsConnection.pageInfo.endCursor) return
    fetchMoreCounts({
      variables: { after: countsConnection.pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          jobCatalog: {
            ...fetchMoreResult.jobCatalog,
            treeNodeAncestorCountsConnection: {
              ...fetchMoreResult.jobCatalog.treeNodeAncestorCountsConnection,
              nodes: [
                ...prev.jobCatalog.treeNodeAncestorCountsConnection.nodes,
                ...fetchMoreResult.jobCatalog.treeNodeAncestorCountsConnection.nodes,
              ],
            },
          },
        }
      },
    })
  }, [countsConnection, fetchMoreCounts])

  const roleSource = useGraphqlDataSource(
    {
      document: JobCatalogRoleSelectorRolesSearchDocument,
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-and-network',
      perPage: ROLE_PAGE_SIZE,
      grouping: {
        mandatory: true,
        collapsible: true,
        defaultOpenGroups: true,
        groupBy: {
          groupId: {
            name: i18n.t('job_catalog.equipment.node_types.function'),
            label: (groupId) => {
              const id = String(groupId)
              return groupNamesRef.current.get(id) ?? id
            },
            itemCount: (groupId) => previousCountsRef.current.get(String(groupId)),
          },
        },
      },
      mapData: (data) => {
        return data.jobCatalog.treeNodesConnection.nodes
          .filter(
            (node): node is typeof node & { ancestor: { uuid: string; name: string | null } } =>
              !!node.ancestor?.uuid
          )
          .map((node): RoleRecord => {
            if (node.ancestor.name) {
              groupNamesRef.current.set(node.ancestor.uuid, node.ancestor.name)
            }
            return {
              id: node.uuid,
              name: node.name ?? '',
              groupId: node.ancestor.uuid,
            }
          })
      },
      mapPagination: (data) => {
        const connection = data.jobCatalog.treeNodesConnection
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? '0',
          hasNextPage: !!connection.pageInfo.hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        search: search || undefined,
        first: ROLE_PAGE_SIZE,
        ...(groupByNodeType ? { groupByNodeType } : {}),
        ...filters,
      }),
    },
    []
  )

  const { data: levelsData, loading: levelsLoading } = useQuery(
    AtsJobCatalogSelectorGetLevelsByRoleDocument,
    {
      variables: { roleUuid: internalRoleUuid ?? '' },
      skip: !internalRoleUuid || !showLevelSelector,
    }
  )

  const levelOptions = useMemo(
    () =>
      (levelsData?.jobCatalog.treeNodesConnection.nodes ?? []).map((level) => ({
        label: level.name ?? '',
        value: level.uuid,
      })),
    [levelsData]
  )

  const mapRoleOptions = useCallback(
    (record: RoleRecord): F0SelectItemProps<string, RoleRecord> => ({
      value: record.id,
      label: record.name,
    }),
    []
  )

  const handleRoleChange = useCallback(
    (value: string) => {
      setInternalRoleUuid(value || undefined)
      onRoleChange(value)
      onLevelChange?.(undefined)
    },
    [onRoleChange, onLevelChange]
  )

  const handleLevelChange = useCallback(
    (value: string) => {
      onLevelChange?.(value)
    },
    [onLevelChange]
  )

  const handleRoleSearchChange = useCallback((search: string) => {
    setRoleSearch(search)
  }, [])

  return (
    <Grid variant="fixed">
      <Box gridColSpan={{ xxs: 1, xs: showLevelSelector ? 6 : 12 }}>
        <F0Select
          label={i18n.t('job_catalog.job_catalog_tree_selector.role.label')}
          name="job_catalog_role"
          placeholder={i18n.t('job_catalog.job_catalog_tree_selector.role.trigger_placeholder')}
          size="md"
          value={selectedRoleUuid}
          onChange={handleRoleChange}
          source={roleSource}
          mapOptions={mapRoleOptions}
          showSearchBox
          onSearchChange={handleRoleSearchChange}
          searchBoxPlaceholder={i18n.t(
            'job_catalog.job_catalog_tree_selector.role.search_placeholder'
          )}
          searchEmptyMessage={i18n.t('job_catalog.job_catalog_tree_selector.role.not_found_title')}
          disabled={disabled}
          readOnly={readOnly}
        />
      </Box>
      {showLevelSelector && (
        <Box gridColSpan={{ xxs: 1, xs: 6 }}>
          <F0Select
            label={i18n.t('job_catalog.job_catalog_tree_selector.level.label')}
            name="job_role_level"
            placeholder={i18n.t('job_catalog.job_catalog_tree_selector.level.placeholder')}
            loading={levelsLoading}
            onChange={handleLevelChange}
            size="md"
            value={selectedRoleLevelUuid}
            options={levelOptions}
            disabled={disabled || !internalRoleUuid}
            required={!!internalRoleUuid}
            error={levelError}
            readOnly={readOnly}
          />
        </Box>
      )}
    </Grid>
  )
}
